/**
 * Minimal Source-1 BSP binary reader (CS:S / CS:GO, versions 19–21). Reads the
 * header + 64-entry lump directory and exposes typed struct iteration over a
 * DataView. Only the lumps Fall Line needs to make a map *playable* are decoded
 * (geometry, collision brushes, entities) — see `lumps.ts`. Format reference:
 * developer.valvesoftware.com/wiki/BSP_(Source).
 */

/** Lump indices we use. */
export const LUMP = {
  ENTITIES: 0,
  PLANES: 1,
  TEXDATA: 2,
  VERTEXES: 3,
  TEXINFO: 6,
  FACES: 7,
  EDGES: 12,
  SURFEDGES: 13,
  MODELS: 14,
  BRUSHES: 18,
  BRUSHSIDES: 19,
  DISPINFO: 26,
  DISP_VERTS: 33,
} as const;

/** 'VBSP' as a little-endian uint32 (bytes V,B,S,P). */
const VBSP_IDENT = 0x50534256;

interface LumpDir {
  offset: number;
  length: number;
  version: number;
  /** Nonzero (the 'LZMA' fourCC) when the lump is LZMA-compressed in place. */
  fourCC: number;
}

/** Structural lumps the importer decodes — if any is LZMA-compressed we can't read it. */
const STRUCTURAL_LUMPS = [0, 1, 3, 6, 7, 12, 13, 14, 18, 19, 26, 33];

export class BspReader {
  readonly view: DataView;
  readonly version: number;
  private readonly lumps: LumpDir[] = [];
  private readonly decoder = new TextDecoder('latin1');

  constructor(buffer: ArrayBuffer) {
    if (buffer.byteLength < 1036) {
      throw new Error('Not a Source BSP: file too small');
    }
    this.view = new DataView(buffer);
    const ident = this.view.getUint32(0, true);
    if (ident !== VBSP_IDENT) {
      throw new Error(this.describeBadIdent(ident));
    }
    this.version = this.view.getInt32(4, true);
    for (let i = 0; i < 64; i++) {
      const o = 8 + i * 16;
      this.lumps.push({
        offset: this.view.getInt32(o, true),
        length: this.view.getInt32(o + 4, true),
        version: this.view.getInt32(o + 8, true),
        fourCC: this.view.getInt32(o + 12, true),
      });
    }
  }

  /**
   * Turn a non-VBSP ident into an actionable message. The common cause is a file
   * that isn't a raw, uncompressed Source-1 `.bsp`: FastDL ships `.bsp.bz2`, sites
   * ship `.zip`/`.rar`, and CS 1.6 (GoldSrc) / CS2 (Source 2 VPK) use different
   * formats entirely.
   */
  private describeBadIdent(ident: number): string {
    const b = [
      this.view.getUint8(0),
      this.view.getUint8(1),
      this.view.getUint8(2),
      this.view.getUint8(3),
    ];
    const is = (...sig: number[]): boolean => sig.every((v, i) => b[i] === v);
    if (is(0x42, 0x5a, 0x68)) {
      return 'This is a bzip2-compressed map (.bsp.bz2 — the FastDL format). Decompress it to a raw .bsp first (e.g. 7-Zip, or `bunzip2 map.bsp.bz2`), then import.';
    }
    if (is(0x50, 0x4b)) {
      return 'This is a .zip archive, not a .bsp. Extract the .bsp inside it first, then import.';
    }
    if (is(0x52, 0x61, 0x72, 0x21)) {
      return 'This is a .rar archive, not a .bsp. Extract the .bsp inside it first, then import.';
    }
    if (is(0x1f, 0x8b)) {
      return 'This is a gzip-compressed file. Decompress it to a raw .bsp first, then import.';
    }
    if (ident === 0x55aa1234) {
      return 'This looks like a Source 2 / CS2 VPK, not a Source 1 .bsp. Only Source 1 maps (CS:S / CS:GO) are supported.';
    }
    if (ident === 29 || ident === 30) {
      return 'This looks like a GoldSrc map (Half-Life 1 / CS 1.6). Only Source 1 maps (CS:S / CS:GO) are supported.';
    }
    const chars = b.map((c) => (c >= 32 && c < 127 ? String.fromCharCode(c) : '·')).join('');
    return `Not a Source BSP: expected ident 'VBSP' but got '${chars}'. Needs an uncompressed Source 1 .bsp (CS:S / CS:GO).`;
  }

  /**
   * True if any structural lump we decode is stored LZMA-compressed (nonzero
   * fourCC). We don't have an LZMA decoder, so such maps can't be imported yet —
   * callers surface a clear message rather than reading compressed bytes as garbage.
   */
  hasCompressedLumps(): boolean {
    return STRUCTURAL_LUMPS.some((i) => (this.lumps[i]?.fourCC ?? 0) !== 0);
  }

  private lumpDir(index: number): LumpDir {
    const l = this.lumps[index];
    if (!l) {
      throw new Error(`BSP lump ${index} out of range`);
    }
    if (l.offset < 0 || l.offset + l.length > this.view.byteLength) {
      throw new Error(`BSP lump ${index} out of bounds`);
    }
    return l;
  }

  /** Number of `stride`-byte structs in a lump. */
  count(index: number, stride: number): number {
    return Math.floor(this.lumpDir(index).length / stride);
  }

  /**
   * Decode every `stride`-byte struct in a lump via `fn(byteOffset, index)`.
   * Returns [] for empty/absent lumps.
   */
  readStructs<T>(index: number, stride: number, fn: (offset: number, index: number) => T): T[] {
    const l = this.lumpDir(index);
    const n = Math.floor(l.length / stride);
    const out: T[] = new Array(n);
    for (let i = 0; i < n; i++) {
      out[i] = fn(l.offset + i * stride, i);
    }
    return out;
  }

  /** Decode a lump as a latin1 string, trimmed at the first NUL (entities lump). */
  readString(index: number): string {
    const l = this.lumpDir(index);
    const bytes = new Uint8Array(this.view.buffer, l.offset, l.length);
    const nul = bytes.indexOf(0);
    return this.decoder.decode(nul === -1 ? bytes : bytes.subarray(0, nul));
  }
}
